import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AVATAR_PALETTE } from '../../constants/avatarPalette';
import { CoinsIcon } from '../../assets/icons/CoinsIcon';
import ExpertPlan from '../../components/expertComponents/expertPlan';
import Modal from '../../components/Modal';
import { useTranslation } from '../../contexts/TranslationContext';
import { TipsterTier } from '../../types/interfaces';
import { apiGet, apiPost } from '../../utils/api';

interface ConnectStatus {
  stripe_account_id: string | null;
  onboarding_complete: boolean;
}

const TierColorPicker: React.FC<{
  value: string | null;
  onChange: (hex: string | null) => void;
  label: string;
}> = ({ value, onChange, label }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div className="expert-plan-form__color-picker" ref={wrapRef}>
      <div className="expert-plan-form__color-swatch-wrap">
        <button
          type="button"
          className={`expert-plan-form__color-swatch${!value ? ' expert-plan-form__color-swatch--empty' : ''}`}
          style={value ? { backgroundColor: value } : undefined}
          onClick={() => setOpen(s => !s)}
          aria-label={label}
        />
        {open && (
          <div className="expert-plan-form__color-popover">
            {AVATAR_PALETTE.map(hex => (
              <button
                key={hex}
                type="button"
                className={`expert-plan-form__color-cell${value === hex ? ' expert-plan-form__color-cell--selected' : ''}`}
                style={{ backgroundColor: hex }}
                onClick={() => { onChange(hex); setOpen(false); }}
              />
            ))}
          </div>
        )}
      </div>
      {value && (
        <button type="button" className="expert-plan-form__color-none-btn" onClick={() => onChange(null)}>
          none
        </button>
      )}
    </div>
  );
};

const ManageMonetization: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState<TipsterTier[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [connectStatus, setConnectStatus] = useState<ConnectStatus | null>(null);
  const [newPlanForm, setNewPlanForm] = useState({
    level: 0,
    name: '',
    priceMonthly: '',
    featuresDescription: '',
    featuresList: [] as string[],
    featured: false,
    color: null as string | null,
  });

  const fetchTiers = useCallback(async () => {
    try {
      const data = await apiGet<TipsterTier[]>('/tipsters/me/tiers');
      setTiers(data);
    } catch (error) {
      console.error('Failed to fetch tiers:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchConnectStatus = useCallback(async () => {
    try {
      const data = await apiGet<ConnectStatus>('/tipsters/connect/status');
      setConnectStatus(data);
    } catch {
      setConnectStatus(null);
    }
  }, []);

  useEffect(() => {
    fetchTiers();
    fetchConnectStatus();
  }, [fetchTiers, fetchConnectStatus]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stripeParam = params.get('stripe');
    if (stripeParam === 'success') {
      fetchConnectStatus();
      navigate('/expert-panel/monetization', { replace: true });
    } else if (stripeParam === 'refresh') {
      handleConnect();
      navigate('/expert-panel/monetization', { replace: true });
    }
  }, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const data = await apiPost<{ onboarding_url: string }>('/tipsters/connect/onboard');
      window.location.href = data.onboarding_url;
    } catch (error) {
      console.error('Failed to start Stripe onboarding:', error);
      setConnecting(false);
    }
  };

  const isFreeTier = Number(newPlanForm.level) === 0;

  const handleNewPlanChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setNewPlanForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFeatureChange = (index: number, value: string) => {
    setNewPlanForm(prev => {
      const next = [...prev.featuresList];
      next[index] = value;
      return { ...prev, featuresList: next };
    });
  };

  const addFeature = () => {
    setNewPlanForm(prev => ({ ...prev, featuresList: [...prev.featuresList, ''] }));
  };

  const removeFeature = (index: number) => {
    setNewPlanForm(prev => ({
      ...prev,
      featuresList: prev.featuresList.filter((_, i) => i !== index),
    }));
  };

  const isFormValid = () => {
    if (!newPlanForm.name.trim()) return false;
    if (!isFreeTier && (!newPlanForm.priceMonthly || Number(newPlanForm.priceMonthly) <= 0)) return false;
    return true;
  };

  const handleAddPlan = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid()) return;
    setSaving(true);
    try {
      await apiPost('/tipsters/me/tiers', {
        level: Number(newPlanForm.level),
        name: newPlanForm.name,
        price_monthly: isFreeTier ? 0 : Number(newPlanForm.priceMonthly),
        features_description: newPlanForm.featuresDescription || null,
        features_list: newPlanForm.featuresList.filter(f => f.trim()),
        featured: newPlanForm.featured,
        color: newPlanForm.color || null,
      });
      setIsAddModalOpen(false);
      setNewPlanForm({ level: 0, name: '', priceMonthly: '', featuresDescription: '', featuresList: [], featured: false, color: null });
      fetchTiers();
    } catch (error) {
      console.error('Failed to create tier:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="expert-panel">
      <div className="expert-panel__container">
        <section className="manage-monetization__connect">
          {connectStatus?.onboarding_complete ? (
            <>
              <div className="manage-monetization__connect-badge">
                <CoinsIcon className="manage-monetization__connect-icon" />
                <span>{t.manageMonetization.connectSuccess}</span>
              </div>
              <p className="manage-monetization__connect-desc">{t.manageMonetization.connectSuccessDesc}</p>
            </>
          ) : (
            <>
              <button
                type="button"
                className="manage-monetization__connect-btn button_primary"
                onClick={handleConnect}
                disabled={connecting}
              >
                <CoinsIcon className="manage-monetization__connect-icon" />
                <span>
                  {connecting
                    ? t.manageMonetization.connecting
                    : connectStatus?.stripe_account_id
                      ? t.manageMonetization.connectPending
                      : t.manageMonetization.connect}
                </span>
              </button>
              <p className="manage-monetization__connect-desc">
                {connectStatus?.stripe_account_id
                  ? t.manageMonetization.connectPendingDesc
                  : t.manageMonetization.connectDescription}
              </p>
            </>
          )}
        </section>
        <section className="manage-monetization__section monetization__current-plans">
          <h2 className="manage-monetization__section-title italic-title">{t.manageMonetization.currentPlans}</h2>
          <button
            className='button_primary'
            onClick={() => setIsAddModalOpen(true)}
            disabled={!connectStatus?.onboarding_complete}
            title={!connectStatus?.onboarding_complete ? 'Connect Stripe first to add tiers' : undefined}
          >
            {t.manageMonetization.addNew} +
          </button>
          {!connectStatus?.onboarding_complete && (
            <p className="manage-monetization__stripe-required">
              Connect Stripe above to start adding paid tiers.
            </p>
          )}
          <div className="manage-monetization__current-plans-grid">
            {loading ? (
              <p>{t.common.loading}</p>
            ) : tiers.length > 0 ? (
              tiers.map((tier) => (
                <ExpertPlan
                  key={tier.id}
                  id={tier.id}
                  level={tier.level}
                  name={tier.name}
                  priceMonthly={tier.price_monthly}
                  featuresDescription={tier.features_description}
                  featuresList={tier.features_list}
                  featured={tier.featured}
                  color={tier.color}
                  onUpdate={fetchTiers}
                />
              ))
            ) : (
              <p>{t.manageMonetization.noPlans}</p>
            )}
          </div>
        </section>
      </div>

      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title={t.manageMonetization.addNew}>
        <form className="expert-plan-form" onSubmit={handleAddPlan}>
          <div className="expert-plan-form__field">
            <label>{t.monetization.level}</label>
            <input
              type="number"
              name="level"
              className="expert-plan-form__input"
              value={newPlanForm.level}
              onChange={handleNewPlanChange}
              min={0}
            />
          </div>
          <div className="expert-plan-form__field">
            <label>{t.monetization.nameRequired}</label>
            <input
              type="text"
              name="name"
              className="expert-plan-form__input"
              value={newPlanForm.name}
              onChange={handleNewPlanChange}
              placeholder={t.monetization.namePlaceholder}
              required
            />
          </div>
          <div className="expert-plan-form__field">
            <label>{t.monetization.priceMonthly} {!isFreeTier && '*'}</label>
            {isFreeTier ? (
              <div className="expert-plan-form__free-badge">{t.tiers.free}</div>
            ) : (
              <input
                type="number"
                name="priceMonthly"
                className="expert-plan-form__input"
                value={newPlanForm.priceMonthly}
                onChange={handleNewPlanChange}
                min={0.01}
                step={0.01}
                placeholder={t.monetization.pricePlaceholder}
                required
              />
            )}
          </div>
          <div className="expert-plan-form__field">
            <label>{t.monetization.description}</label>
            <textarea
              name="featuresDescription"
              className="expert-plan-form__textarea"
              value={newPlanForm.featuresDescription}
              onChange={handleNewPlanChange}
              placeholder={t.monetization.descriptionPlaceholder}
              rows={3}
            />
          </div>
          <div className="expert-plan-form__field">
            <label>{t.monetization.featuresList}</label>
            <div className="expert-plan-form__features-list">
              {newPlanForm.featuresList.map((feat, i) => (
                <div className="expert-plan-form__feature-row" key={i}>
                  <input
                    type="text"
                    className="expert-plan-form__input"
                    value={feat}
                    onChange={e => handleFeatureChange(i, e.target.value)}
                    placeholder={t.monetization.featurePlaceholder}
                  />
                  <button
                    type="button"
                    className="expert-plan-form__feature-remove"
                    onClick={() => removeFeature(i)}
                    aria-label="Remove"
                  >
                    ×
                  </button>
                </div>
              ))}
              <button type="button" className="expert-plan-form__add-feature-btn" onClick={addFeature}>
                + {t.monetization.addFeature}
              </button>
            </div>
          </div>
          <div className="expert-plan-form__field">
            <label>{t.monetization.tierColor}</label>
            <TierColorPicker
              value={newPlanForm.color}
              onChange={c => setNewPlanForm(prev => ({ ...prev, color: c }))}
              label={t.monetization.tierColor}
            />
          </div>
          <div className="expert-plan-form__field">
            <div className="expert-plan-form__switch-row">
              <label>{t.monetization.featured}</label>
              <label className="expert-plan-form__switch">
                <input
                  type="checkbox"
                  checked={newPlanForm.featured}
                  onChange={e => setNewPlanForm(prev => ({ ...prev, featured: e.target.checked }))}
                />
                <span className="expert-plan-form__switch-track" />
              </label>
            </div>
          </div>
          <div className="expert-plan-form__actions">
            <button type="button" className="button_secondary" onClick={() => setIsAddModalOpen(false)} disabled={saving}>
              {t.common.cancel}
            </button>
            <button type="submit" className="button_primary" disabled={saving || !isFormValid()}>
              {saving ? t.expertPanel.saving : t.manageMonetization.addNew}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ManageMonetization;
